// Per-account signature management — multiple named signatures per account,
// one default. Editor: name + HTML side-by-side with live preview.

import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Mail, Pencil, Plus, Save, Star, Trash2, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAccounts, type GmailAccount } from '@/lib/accounts';
import {
  signaturesForAccount,
  useCreateSignature,
  useDeleteSignature,
  useSignatures,
  useUpdateSignature,
  type SignatureRecord,
} from '@/lib/signatures';

const DEFAULT_TEMPLATE = `<div style="font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #1f2937;">
  <div style="font-weight: 600;">Your Name</div>
  <div>Job title · Company</div>
  <div><a href="mailto:you@example.com" style="color: #0284c7;">you@example.com</a></div>
</div>`;

export function SignaturesSettings() {
  const accounts = useAccounts();
  const sigs = useSignatures();

  if (accounts.isLoading || sigs.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading signatures…
        </CardContent>
      </Card>
    );
  }

  const accs = accounts.data?.accounts ?? [];
  if (accs.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          Connect a Gmail account first — signatures attach to a specific inbox.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Email signatures
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-xs text-muted-foreground">
          Each inbox can have multiple named signatures — the default is appended
          automatically; the composer lets you switch per-email.
        </p>
        {accs.map((a, i) => (
          <div key={a.id} className="space-y-3">
            {i > 0 && <Separator />}
            <AccountSignatures
              account={a}
              signatures={signaturesForAccount(sigs.data?.signatures, a.id)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AccountSignatures({
  account,
  signatures: list,
}: {
  account: GmailAccount;
  signatures: SignatureRecord[];
}) {
  const [editing, setEditing] = useState<SignatureRecord | 'new' | null>(null);
  const update = useUpdateSignature();
  const del = useDeleteSignature();
  const Icon = account.kind === 'shared' ? Users : Mail;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        <span className="truncate text-sm font-medium">{account.emailAddress}</span>
        {account.kind === 'shared' && (
          <Badge variant="secondary" className="h-4 px-1.5 text-[9px]">
            shared
          </Badge>
        )}
        <div className="ml-auto">
          <Button
            size="xs"
            variant="outline"
            className="gap-1"
            onClick={() => setEditing('new')}
            disabled={editing !== null}
          >
            <Plus className="size-3" strokeWidth={2} />
            Add signature
          </Button>
        </div>
      </div>

      {list.length === 0 && !editing && (
        <p className="text-xs text-muted-foreground">No signatures yet for this inbox.</p>
      )}

      {list.map((sig) => (
        <div key={sig.id} className="flex items-center gap-2 rounded-md border bg-card/50 px-3 py-2">
          <button
            type="button"
            onClick={() => {
              if (!sig.isDefault) {
                update.mutate(
                  { id: sig.id, isDefault: true },
                  { onSuccess: () => toast.success(`${sig.name} is now the default`) },
                );
              }
            }}
            className="shrink-0"
            title={sig.isDefault ? 'Default signature' : 'Make default'}
            aria-label={sig.isDefault ? 'Default signature' : 'Make default'}
          >
            <Star
              className={`size-4 ${sig.isDefault ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40 hover:text-amber-400'}`}
              strokeWidth={1.75}
            />
          </button>
          <span className="min-w-0 flex-1 truncate text-sm">{sig.name}</span>
          {sig.isDefault && (
            <Badge variant="outline" className="h-4 gap-1 px-1.5 text-[9px]">
              <Check className="size-2.5" strokeWidth={2.5} />
              Default
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setEditing(sig)}
            disabled={editing !== null}
            aria-label="Edit signature"
          >
            <Pencil className="size-3.5" strokeWidth={1.75} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-destructive"
            onClick={() => {
              if (confirm(`Delete signature "${sig.name}"?`)) del.mutate(sig.id);
            }}
            disabled={del.isPending || editing !== null}
            aria-label="Delete signature"
          >
            <Trash2 className="size-3.5" strokeWidth={1.75} />
          </Button>
        </div>
      ))}

      {editing && (
        <SignatureEditor
          accountId={account.id}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function SignatureEditor({
  accountId,
  initial,
  onClose,
}: {
  accountId: number;
  initial: SignatureRecord | null;
  onClose: () => void;
}) {
  const create = useCreateSignature();
  const update = useUpdateSignature();
  const [name, setName] = useState(initial?.name ?? '');
  const [draft, setDraft] = useState(initial?.html ?? DEFAULT_TEMPLATE);

  useEffect(() => {
    setName(initial?.name ?? '');
    setDraft(initial?.html ?? DEFAULT_TEMPLATE);
  }, [initial]);

  const pending = create.isPending || update.isPending;

  function submit() {
    const html = draft.trim();
    const trimmedName = name.trim() || 'Default';
    if (!html) {
      toast.error('Signature is empty');
      return;
    }
    const onSuccess = () => {
      toast.success('Signature saved');
      onClose();
    };
    const onError = (e: Error) => toast.error(e.message);
    if (initial) {
      update.mutate({ id: initial.id, name: trimmedName, html }, { onSuccess, onError });
    } else {
      create.mutate({ accountId, name: trimmedName, html }, { onSuccess, onError });
    }
  }

  return (
    <div className="space-y-3 rounded-md border bg-card/50 p-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Signature name — e.g. Formal, Short, Pressific"
          maxLength={60}
          className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <Button size="xs" variant="ghost" onClick={onClose} disabled={pending}>
          <X className="size-3" strokeWidth={2.5} />
          Cancel
        </Button>
        <Button size="xs" onClick={submit} disabled={pending} className="gap-1">
          {pending ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" strokeWidth={2} />}
          Save
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            HTML
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            spellCheck={false}
            className="block w-full resize-y rounded-md border bg-background px-3 py-2 font-mono text-[11px] outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Preview
          </div>
          <SignaturePreview html={draft || DEFAULT_TEMPLATE} />
        </div>
      </div>
    </div>
  );
}

function SignaturePreview({ html }: { html: string }) {
  const srcdoc = useMemo(
    () => `<!doctype html><meta charset="utf-8"><base target="_blank">
<style>html,body{margin:0;padding:8px 10px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif;font-size:13px;color:#1f2937;background:white;}</style>
<body>${html}</body>`,
    [html],
  );
  return (
    <iframe
      title="Signature preview"
      srcDoc={srcdoc}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      className="block w-full rounded-md border bg-white"
      style={{ height: 180 }}
    />
  );
}
